---
title: "7 CRM features small teams overpay for and don't use"
description: "From running a phone-heavy sales team and watching dozens of small sales teams pick CRMs, the features they pay for and never click. What to skip on your shortlist."
seoDescription: "Seven CRM features small sales teams pay for and rarely use, and what a phone-heavy team actually needs from a CRM instead."
publishedAt: "2026-05-31"
slug: "crm-features-small-teams-overpay"
authorPhoto: "/og/sohel-headshot.jpg"
authorSameAs:
  - { label: "LinkedIn", url: "https://www.linkedin.com/in/mdsohelrana/" }
---

# 7 CRM features small teams overpay for and don't use

Every CRM pricing page is a feature ladder. The cheapest tier is missing things; the next tier up has them; the top tier has *everything*. If you're a 5–25 seat team picking your first or second CRM, you'll be tempted to climb that ladder. Don't.

Here are the seven features I see small teams pay for over and over, and never use. From running Biddaan's sales and operations team and watching dozens of customers pick CRMs since.

## 1. Marketing automation workflows

The pitch: "Build email drip campaigns, lead-scoring rules, and behavior-based triggers."

The reality: building a working drip campaign is a half-time-job for a marketing ops person. If you don't have one, you'll set up two campaigns in week one, never iterate on them, and forget you're paying $50/month extra for the privilege.

Skip it. Use a dedicated email tool (Buttondown, MailerLite, ConvertKit) when you actually need a drip — usually a year in.

## 2. AI-generated email drafts

The pitch: "AI writes your prospecting emails, summarizes calls, predicts deal close dates."

The reality: the email drafts read like AI — because they are. Prospects can tell. The summaries are okay but rarely save time. Predicted close dates are wishful at best.

Skip it. Spend the equivalent monthly fee on a single 30-minute coaching call with a senior rep instead.

## 3. Multi-currency and tax handling

The pitch: "Sell in 50 currencies; auto-calculate VAT and sales tax."

The reality: if you're a small team, you sell in one or two currencies. Tax is something your accountant figures out at month-end from invoice exports.

Skip it unless you're actually international today.

## 4. Custom fields with conditional visibility

The pitch: "Show different fields based on stage, deal type, region."

The reality: small teams create three custom fields, never use the conditional visibility, and end up with a contact form that's three fields long.

Skip it. A flat list of custom fields is fine.

## 5. Forecasting and quota management

The pitch: "Set quotas; track attainment; forecast next quarter."

The reality: forecasting is theatre below a certain team size. The owner already knows whether the month will close, and the rep already knows whether they'll hit their number. The dashboard adds noise without changing decisions.

Skip it until you're 25+ seats.

## 6. Roles, permissions, and granular access controls

The pitch: "Restrict who can see deals over $X, who can edit pipelines, who can export data."

The reality: at small-team scale you don't have these problems. Everyone sees everything. The day you do, you'll know it because someone exfiltrated your contact list — and a permission scheme wouldn't have stopped them.

Skip it unless you have a specific compliance reason.

## 7. Customer support / ticketing modules

The pitch: "Manage support tickets right inside your CRM."

The reality: support tickets and sales pipelines have different lifecycles, owners, and SLAs. Stuffing both into one tool means neither gets a great UI. Use a dedicated support tool (Help Scout, Plain, Crisp) and link it to the CRM via tags.

Skip it.

## What to actually pay for

The features that earn their keep at small-team scale, in my order of payback:

1. **Working call recording** — the conversation is the asset
2. **A clean Kanban view** — drag deals, click cards, log calls
3. **CSV import that doesn't fight you** — you'll do this every month
4. **Reports that answer one question well** — pipeline by stage, by rep, by source
5. **Predictable, flat pricing** — so the question isn't "which features can we afford"

Build your shortlist around these. Most CRMs do (1) badly or not at all; that's the one differentiator most small teams should index on.

## Why this list exists

I run NerdCRM. I made it $4/seat flat with no per-feature tiers because I was tired of helping friends choose between Bigin's Express, Standard, and Premier tiers based on a feature checklist nobody actually uses. If a feature is worth shipping, ship it to everyone. If it isn't, don't.

The [live demo](/demo) is seeded with a phone-heavy sales workspace if you want to see what's actually there. No signup.
